import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { get } from "@/lib/api";
import { PageLoader, PageHeader, EmptyState } from "@/components/common";
import { BookOpen, Users, Lock, PlayCircle } from "lucide-react";

const COURSE_IMG = "https://images.unsplash.com/photo-1741699428220-65f37f3fbbcb?crop=entropy&cs=srgb&fm=jpg&q=85&w=300";

export default function Courses() {
  const nav = useNavigate();
  const { data: courses, isLoading } = useQuery({ queryKey: ["courses"], queryFn: () => get("/courses") });
  const { data: subs } = useQuery({ queryKey: ["mysubs"], queryFn: () => get("/my/subscriptions") });
  const { data: enrolls } = useQuery({ queryKey: ["myenroll"], queryFn: () => get("/my/enrollments") });
  const subscribed = (subs || []).some((s) => s.status === "active");
  const enrolledIds = new Set((enrolls || []).map((e) => e.item_id));

  const open = (c) => {
    const owned = c.is_free || subscribed || enrolledIds.has(c.id);
    if (!owned) { toast.info("Enroll with a subscription to unlock this course"); nav("/app/subscriptions"); return; }
    nav(`/app/courses/${c.id}`);
  };

  return (
    <div>
      <PageHeader title="Courses" subtitle="Structured video courses by top faculty — unlock all with a subscription" />
      {isLoading ? <PageLoader /> : !courses || courses.length === 0 ? <EmptyState icon={BookOpen} title="No courses found" /> : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {courses.map((c) => {
            const owned = c.is_free || subscribed || enrolledIds.has(c.id);
            return (
              <div key={c.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden card-hover flex flex-col" data-testid={`course-card-${c.id}`}>
                <img src={c.thumbnail || COURSE_IMG} alt={c.title} className="h-24 w-full object-cover" />
                <div className="p-3 flex-1 flex flex-col">
                  <h3 className="font-display font-bold text-slate-900 text-sm leading-snug line-clamp-2">{c.title}</h3>
                  <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1"><Users className="h-3 w-3" /> {c.enroll_count} enrolled</p>
                  <button onClick={() => open(c)} data-testid={`course-action-${c.id}`}
                    className={`mt-auto w-full font-bold py-2 rounded-lg text-xs flex items-center justify-center gap-1 ${owned ? "bg-indigo-800 hover:bg-indigo-900 text-white" : "bg-sky-500 hover:bg-sky-600 text-white"}`}>
                    {owned ? <><PlayCircle className="h-3.5 w-3.5" /> Start Learning</> : <><Lock className="h-3.5 w-3.5" /> Enroll Now</>}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
